import { EmissionEstimationIcons } from '@lune-fe/lune-components-lib'
import { AutocompleteSelect, LuneThemeNew, Text, Tile } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import {
    airTransportModeArray,
    inlandWaterwaysTransportModeArray,
    railTransportModeArray,
    roadTransportModeArray,
    seaTransportModeArray,
    TransportMode,
    TransportModeGroup as OriginalTransportModeGroup,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'

type TransportModeSelectProps = {
    transportModeSelected: TransportMode | 'Select a column' | undefined
    selectedGroup?: TransportModeGroup
    onUpdate: (group: TransportModeGroup | undefined) => void
    transportModeError?: string
}

export type TransportModeGroup = OriginalTransportModeGroup | 'Select a column'

type ListOption = {
    value: TransportModeGroup | 'Select a column'
    label: string
    icon: string
    group: 'column' | 'default'
}

const options: ListOption[] = [
    {
        label: 'Select a column',
        value: 'Select a column',
        icon: 'tableColumns',
        group: 'column',
    },
    {
        label: 'Sea',
        value: 'Sea',
        icon: 'ship',
        group: 'default',
    },
    {
        label: 'Air',
        value: 'Air',
        icon: 'plane',
        group: 'default',
    },
    {
        label: 'Road',
        value: 'Road',
        icon: 'truck',
        group: 'default',
    },
    {
        label: 'Rail',
        value: 'Rail',
        icon: 'train',
        group: 'default',
    },
    {
        label: 'Inland waterways',
        value: 'Inland waterways',
        icon: 'waterway',
        group: 'default',
    },
]

function transportArrayToRecord<T extends readonly TransportMode[], V extends TransportModeGroup>(
    arr: T,
    value: V,
): Record<T[number], V> {
    return Object.fromEntries(arr.map((k) => [k, value])) as Record<T[number], V>
}

export const transportModeToGroupMapping: Record<TransportMode, OriginalTransportModeGroup> = {
    ...transportArrayToRecord(airTransportModeArray, 'Air'),
    ...transportArrayToRecord(roadTransportModeArray, 'Road'),
    ...transportArrayToRecord(railTransportModeArray, 'Rail'),
    ...transportArrayToRecord(seaTransportModeArray, 'Sea'),
    ...transportArrayToRecord(inlandWaterwaysTransportModeArray, 'Inland waterways'),
}

const { palette } = LuneThemeNew

const TransportModeGroupSelect = ({
    transportModeError,
    transportModeSelected,
    selectedGroup: parentSelectedGroup,
    onUpdate,
}: TransportModeSelectProps) => {
    const [localSelected, setLocalSelected] = useState<string | null>(null)
    const [error, setError] = useState<string | undefined>()

    const selectedGroup = useMemo(() => {
        setError(undefined)

        if (parentSelectedGroup) {
            return parentSelectedGroup
        }

        if (!transportModeSelected) {
            return undefined
        }

        if (transportModeSelected === 'Select a column') {
            return 'Select a column'
        }

        return transportModeToGroupMapping[transportModeSelected]
    }, [transportModeSelected, localSelected, parentSelectedGroup])

    useEffect(() => {
        if (transportModeSelected) {
            setLocalSelected(null)
        }
    }, [transportModeSelected])

    const final = useMemo(() => {
        return options.find((option) => option.value === (selectedGroup || localSelected)) || null
    }, [selectedGroup, localSelected])

    useEffect(() => {
        if (!transportModeError) {
            return
        }
        setError(!final ? 'Mandatory' : undefined)
    }, [transportModeError, final])

    return (
        <Box sx={{ mb: 3 }}>
            <AutocompleteSelect
                error={error}
                label={'Select transport mode'}
                onChange={(selected: ListOption | null) => {
                    setError(undefined)
                    setLocalSelected(selected?.value ?? null)
                    onUpdate(selected?.value)
                }}
                value={final}
                items={options}
                groupBy={(option: ListOption | null) => option?.group || ''}
                showGroupName={false}
                customOptions={(option) => (
                    <Tile
                        icon={
                            EmissionEstimationIcons[
                                option.icon as keyof typeof EmissionEstimationIcons
                            ]
                        }
                        sx={{ '*': { color: palette.Grey900 } }}
                    >
                        <Text variant={'body3'}>{option.label}</Text>
                    </Tile>
                )}
            />
        </Box>
    )
}

export default TransportModeGroupSelect
